import React from 'react';
import { GraduationCapIcon, CheckIcon, LockIcon } from 'lucide-react';
import { areaStudyOptions, FilterOption } from '../data/filterOptions';
import { TrackingNoticeLinks } from './TrackingNoticeLinks';

// Visual shell for the /request-information/v3 template: the marketing
// copy stays pinned on the left while every question is asked inside a
// single persistent card on the right.

export interface SplitCardFormValues {
  areaOfStudy: number;
  program: number;
  highestEducation: string;
  hsGraduationYear: string;
  startDate: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  consent: boolean;
}
export interface RequestInfoSplitCardProps {
  badge: string;
  headline: string;
  headlineAccent: string;
  subhead: string;
  features: string[];
  // Flow position. `step` matches the shared flow (0 = program selection,
  // 1 = education, 2 = grad year, 3 = timeframe, 5 = address, 6 = contact).
  // `stage` splits step 0 into area (0) then program (1).
  step: number;
  stage: number;
  formData: SplitCardFormValues;
  availablePrograms: FilterOption[];
  years: number[];
  error: string;
  tcpaLoading: boolean;
  tcpaError: string;
  tcpaDisclaimerHtml: string | null;
  /** True while the address step's ZIP is being verified. */
  addressChecking?: boolean;
  onFieldChange: (field: keyof SplitCardFormValues, value: string | number | boolean) => void;
  onAreaSelect: (area: number) => void;
  onZipChange: (zip: string) => void;
  onPhoneChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onContinue: () => void;
  onBack: () => void;
}

// Visual Arts & Graphic Design (12) isn't offered on this template.
const HIDDEN_AREA_VALUES = [12];
const AREA_TILES = areaStudyOptions.filter(option => !HIDDEN_AREA_VALUES.includes(option.value));
const STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
const START_OPTIONS: {
  value: string;
  label: string;
}[] = [{
  value: 'immediately',
  label: 'Immediately'
}, {
  value: '1-3-months',
  label: 'Within 1–3 months'
}, {
  value: '3-6-months',
  label: 'Within 3–6 months'
}, {
  value: '6-12-months',
  label: 'Within 6–12 months'
}, {
  value: '1-year-plus',
  label: 'More than a year out'
}, {
  value: 'undecided',
  label: 'Undecided'
}];
const EDUCATION_OPTIONS: {
  value: string;
  label: string;
}[] = [{
  value: 'high-school',
  label: 'High School Diploma/GED'
}, {
  value: 'some-college',
  label: 'Some College'
}, {
  value: 'associates',
  label: "Associate's Degree"
}, {
  value: 'bachelors',
  label: "Bachelor's Degree"
}, {
  value: 'some-high-school',
  label: 'Some High School'
}];
const FALLBACK_CONSENT = 'By checking this box and clicking "See My Matches," I provide my electronic signature and express written consent for findonlinedegrees.org and up to four (4) partner schools to contact me at the phone number and email provided above — including by SMS/text messages and calls using an automatic telephone dialing system or an artificial/prerecorded voice — regarding educational opportunities. Message and data rates may apply. Message frequency varies. Consent is not a condition of enrollment or purchase. I can revoke consent at any time.';
const CARD_COPY: Record<string, {
  title: string;
  subtitle: string;
}> = {
  area: {
    title: "Let's Get Started",
    subtitle: 'Select your area of interest'
  },
  program: {
    title: 'Narrow it down',
    subtitle: 'Pick the program closest to your interest'
  },
  education: {
    title: 'Your background',
    subtitle: 'This helps us match you to programs you qualify for'
  },
  gradYear: {
    title: 'Almost there',
    subtitle: 'What year did you graduate high school?'
  },
  timing: {
    title: 'Timing',
    subtitle: 'When would you like to start classes?'
  },
  address: {
    title: 'Where are you located?',
    subtitle: 'Used to match you with programs available in your state'
  },
  contact: {
    title: 'Almost done',
    subtitle: 'Where should schools reach you?'
  }
};
const selectClasses = (filled: boolean) => `w-full px-4 py-3 bg-white border rounded-xl text-gray-900 appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-[#e8594f]/30 ${filled ? 'border-[#e8594f]' : 'border-gray-200 hover:border-gray-300'}`;
const inputClasses = 'w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 transition-colors placeholder:text-gray-400 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/30 hover:border-gray-300';
const chevronStyle = {
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
  backgroundPosition: 'right 0.75rem center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1.25em 1.25em',
  paddingRight: '2.5rem'
};
const FieldLabel = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-517" data-mp="3k4z9ufm6i9fl">
    {children} <span className="text-[#e8594f]" data-id="element-518" data-mp="3f6xrxy0hog5p">*</span>
  </span>;
export function RequestInfoSplitCard({
  badge,
  headline,
  headlineAccent,
  subhead,
  features,
  step,
  stage,
  formData,
  availablePrograms,
  years,
  error,
  tcpaLoading,
  tcpaError,
  tcpaDisclaimerHtml,
  addressChecking = false,
  onFieldChange,
  onAreaSelect,
  onZipChange,
  onPhoneChange,
  onContinue,
  onBack
}: RequestInfoSplitCardProps) {
  const screen = step === 0 ? stage === 0 ? 'area' : 'program' : step === 1 ? 'education' : step === 2 ? 'gradYear' : step === 3 ? 'timing' : step === 5 ? 'address' : 'contact';
  const displayStep = screen === 'area' ? 1 : screen === 'program' ? 2 : screen === 'education' ? 3 : screen === 'gradYear' ? 4 : screen === 'timing' ? 5 : screen === 'address' ? 6 : 7;
  const canGoBack = !(step === 0 && stage === 0);
  const isFinalStep = screen === 'contact';
  const copy = CARD_COPY[screen];
  return <div className="min-h-screen bg-[#f4f5f7] flex flex-col" data-id="element-519" data-mp="2exfyg7dvzmzf">
      {/* Advertising disclosure */}
      <div className="bg-gray-100 border-b border-gray-200 py-2.5 px-4" data-id="element-520" data-mp="2jw99zu9m53wy">
        <p className="text-center text-xs text-gray-600" data-id="element-521" data-mp="37opra2lj0rv7">
          We earn commissions from schools listed on this site, which influences
          how listings are presented.{' '}
          <a href="/terms" className="underline hover:text-gray-900" data-id="element-522" data-mp="2ymgm7s911qp7">
            Advertising Disclosure
          </a>
        </p>
      </div>

      {/* Brand bar */}
      <header className="bg-white border-b border-gray-200 py-4 px-4" data-id="element-523" data-mp="1n8l368f04xsp">
        <a href="/" className="flex items-center justify-center gap-2.5" data-id="element-524" data-mp="1nafnplmd2nw9">
          <span className="w-9 h-9 rounded-lg bg-[#e8594f] flex items-center justify-center" data-id="element-525" data-mp="1rbhq2bbkrg2g">
            <GraduationCapIcon className="w-5 h-5 text-white" aria-hidden="true" data-id="element-526" data-mp="1joueb9uen85y" />
          </span>
          <span className="text-xl font-bold text-gray-900 tracking-tight" data-id="element-527" data-mp="1eqswstcmngwl">
            FindOnlineDegrees
          </span>
        </a>
      </header>

      <main className="flex-1 px-4 py-8 md:py-16" data-id="element-528" data-mp="1opruxhzrnu5c">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-529" data-mp="1oq1lb6oq43bk">
          {/* Trust badge — sits above the card on mobile, and inside the
              marketing column from lg up. */}
          <div className="order-first lg:hidden text-center" data-id="element-530" data-mp="vyqbh4p1jcg0">
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3.5 py-1.5 rounded-full text-sm font-semibold" data-id="element-531" data-mp="1aqshl6hpf1oh">
              <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-532" data-mp="15wxppfry3geb" />
              {badge}
            </div>
          </div>

          {/* Marketing column — below the card on mobile */}
          <div className="order-2 lg:order-1" data-id="element-533" data-mp="da37kpilot40">
            <div className="hidden lg:block" data-id="element-534" data-mp="3nwxwlu5qly8i">
              <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3.5 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-535" data-mp="3nxzbk9pvj4vi">
                <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-536" data-mp="1pg379tc1u9f" />
                {badge}
              </div>
            </div>
            <h1 className="text-center lg:text-left text-4xl sm:text-5xl font-extrabold text-gray-900 leading-[1.05] tracking-tight mb-6" data-id="element-537" data-mp="5rukvql9mnz4">
              {headline}
              <span className="block text-[#e8594f]" data-id="element-538" data-mp="fopkkct9yuez">{headlineAccent}</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-7 max-w-md" data-id="element-539" data-mp="kfperab0eyds">
              {subhead}
            </p>
            <ul className="space-y-3" data-id="element-540" data-mp="pdqzdzo335rd">
              {features.map(feature => <li key={feature} className="flex items-center gap-3 text-gray-800" data-id="element-541" data-mp="tgvyrtcvarbs">
                  <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-542" data-mp="yf7ak9mq3bwn" />
                  <span data-id="element-543" data-mp="2ix9guac3hwn5">{feature}</span>
                </li>)}
            </ul>
          </div>

          {/* Question card — first on mobile so it lands above the fold */}
          <div className="order-1 lg:order-2 bg-white rounded-2xl shadow-[0_10px_40px_-12px_rgba(15,23,42,0.18)] p-6 sm:p-8" data-id="element-544" data-mp="2e0rax7513s98">
            <div className="text-center mb-6" data-id="element-545" data-mp="24y9p2ttad1mr">
              <p className="font-mono text-xs tracking-[0.18em] text-[#e8594f] mb-2" data-id="element-546" data-mp="24zktmvuk2dex">
                STEP {displayStep} OF 7
              </p>
              <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-547" data-mp="2xlvtu5e92cb7">
                {copy.title}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-548" data-mp="2suw0tb894edz">{copy.subtitle}</p>
            </div>

            {screen === 'area' && <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="group" aria-label="Area of interest" data-id="element-549" data-mp="2nw2sdx7tndko">
                {AREA_TILES.map(option => {
              const selected = formData.areaOfStudy === option.value;
              return <button key={option.value} type="button" onClick={() => onAreaSelect(option.value)} aria-pressed={selected} className={`text-center px-3 py-4 rounded-xl border transition-all ${selected ? 'border-[#e8594f] bg-[#e8594f]/5 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`} data-id="element-550" data-mp="2tdrv3nod0tyb">
                      <span className={`mx-auto mb-2 flex w-4 h-4 items-center justify-center rounded border ${selected ? 'border-[#e8594f] bg-[#e8594f]' : 'border-gray-300 bg-white'}`} aria-hidden="true" data-id="element-551" data-mp="37c8cc8jum2up">
                        {selected && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-552" data-mp="3q5lo6vbvdikg" />}
                      </span>
                      <span className="block text-sm font-semibold text-gray-900 leading-snug" data-id="element-553" data-mp="dm9l7d082agt">
                        {option.label}
                      </span>
                    </button>;
            })}
              </div>}

            {screen === 'program' && <label className="block" data-id="element-554" data-mp="8l3luwc23oyb">
                <FieldLabel data-id="element-555" data-mp="8nznaun44vri">Program of Interest</FieldLabel>
                <select value={formData.program || ''} onChange={e => onFieldChange('program', Number(e.target.value))} className={selectClasses(Boolean(formData.program))} style={chevronStyle} data-id="element-556" data-mp="3qzclaqv9h3ds">
                  <option value="" data-id="element-557" data-mp="3m09otutck5hf">Select a program</option>
                  {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-558" data-mp="382l54qb0n4dy">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'education' && <label className="block" data-id="element-559" data-mp="1dtocll8edk20">
                <FieldLabel data-id="element-560" data-mp="1dsms6q5hrl7m">Highest Education Level</FieldLabel>
                <select value={formData.highestEducation} onChange={e => onFieldChange('highestEducation', e.target.value)} className={selectClasses(Boolean(formData.highestEducation))} style={chevronStyle} data-id="element-561" data-mp="18ttmvl0cz0ij">
                  <option value="" data-id="element-562" data-mp="1wje8ycrt6dw1">Select one</option>
                  {EDUCATION_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-563" data-mp="1shj6v621zwh7">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'gradYear' && <label className="block" data-id="element-564" data-mp="h0sx5cyf8miu">
                <FieldLabel data-id="element-565" data-mp="c3t0ttrdiy4x">HS Graduation Year</FieldLabel>
                <select value={formData.hsGraduationYear} onChange={e => onFieldChange('hsGraduationYear', e.target.value)} className={selectClasses(Boolean(formData.hsGraduationYear))} style={chevronStyle} data-id="element-566" data-mp="c6f7lr6fx9m3">
                  <option value="" data-id="element-567" data-mp="2wq9i136f8x2o">Select year</option>
                  {years.map(year => <option key={year} value={year} data-id="element-568" data-mp="31knl7rrbs8hw">
                      {year}
                    </option>)}
                  <option value="1989" data-id="element-569" data-mp="3bgh2tpjwis6j">1989 or earlier</option>
                </select>
              </label>}

            {screen === 'timing' && <label className="block" data-id="element-570" data-mp="3gdz7v4ussn99">
                <FieldLabel data-id="element-571" data-mp="3khyij6tecrup">Class Start Timeframe</FieldLabel>
                <select value={formData.startDate} onChange={e => onFieldChange('startDate', e.target.value)} className={selectClasses(Boolean(formData.startDate))} style={chevronStyle} data-id="element-572" data-mp="3kfc9t3urs6aw">
                  <option value="" data-id="element-573" data-mp="uro112xcmx98">Select timeframe</option>
                  {START_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-574" data-mp="qtyqnxj9hqjo">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'address' && <div className="space-y-4" data-id="element-575" data-mp="c414to8t1cxn">
                <label className="block" data-id="element-576" data-mp="c0wra09g94ub">
                  <FieldLabel data-id="element-577" data-mp="74o90dzygvix">Street Address</FieldLabel>
                  <input type="text" value={formData.streetAddress} onChange={e => onFieldChange('streetAddress', e.target.value)} placeholder="123 Main St" autoComplete="street-address" className={inputClasses} data-id="element-578" data-mp="7221ged2eg0p" />
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-579" data-mp="3ki9p8916axeq">
                  <label className="block" data-id="element-580" data-mp="2008oniye8c8p">
                    <FieldLabel data-id="element-581" data-mp="1zzfpp45oer14">City</FieldLabel>
                    <input type="text" value={formData.city} onChange={e => onFieldChange('city', e.target.value)} autoComplete="address-level2" className={inputClasses} data-id="element-582" data-mp="24s0ic2cuiy32" />
                  </label>
                  <label className="block" data-id="element-583" data-mp="2isjo12tu9s22">
                    <FieldLabel data-id="element-584" data-mp="2ipf85870h7vk">State</FieldLabel>
                    <select value={formData.state} onChange={e => onFieldChange('state', e.target.value)} className={selectClasses(Boolean(formData.state))} style={chevronStyle} data-id="element-585" data-mp="2np1ovrm1t694">
                      <option value="" data-id="element-586" data-mp="yadc3p9jpmak">State</option>
                      {STATES.map(abbr => <option key={abbr} value={abbr} data-id="element-587" data-mp="13bj5lpb15dl5">
                          {abbr}
                        </option>)}
                    </select>
                  </label>
                  <label className="block" data-id="element-588" data-mp="301ly320y8lgl">
                    <FieldLabel data-id="element-589" data-mp="302wz4u30ff40">ZIP Code</FieldLabel>
                    <input type="text" inputMode="numeric" maxLength={5} value={formData.zipCode} onChange={e => onZipChange(e.target.value.replace(/\D/g, '').slice(0, 5))} autoComplete="postal-code" className={inputClasses} data-id="element-590" data-mp="2v8sskbxnp93g" />
                  </label>
                </div>
              </div>}

            {screen === 'contact' && <div className="space-y-4" data-id="element-591" data-mp="28azdxwrbj22p">
                <div className="grid grid-cols-2 gap-3" data-id="element-592" data-mp="ajd6fu9du8k5">
                  <label className="block" data-id="element-593" data-mp="aitxv45mgwis">
                    <FieldLabel data-id="element-594" data-mp="6ib6tcbjzuhn">First Name</FieldLabel>
                    <input type="text" value={formData.firstName} onChange={e => onFieldChange('firstName', e.target.value)} autoComplete="given-name" className={inputClasses} data-id="element-595" data-mp="6it7cl590uvd" />
                  </label>
                  <label className="block" data-id="element-596" data-mp="226td15jlzlpw">
                    <FieldLabel data-id="element-597" data-mp="225rr26121ete">Last Name</FieldLabel>
                    <input type="text" value={formData.lastName} onChange={e => onFieldChange('lastName', e.target.value)} autoComplete="family-name" className={inputClasses} data-id="element-598" data-mp="15h26g7y1m83q" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-599" data-mp="1ji4ugq8llsha">
                  <label className="block" data-id="element-600" data-mp="2px5i88ngz4cs">
                    <FieldLabel data-id="element-601" data-mp="2pwdxxtzbot1t">Email Address</FieldLabel>
                    <input type="email" value={formData.email} onChange={e => onFieldChange('email', e.target.value)} autoComplete="email" className={inputClasses} data-id="element-602" data-mp="2uuxl1xpnkavr" />
                  </label>
                  <label className="block" data-id="element-603" data-mp="2fc9ze5vf6fyz">
                    <FieldLabel data-id="element-604" data-mp="2akrzll54ulfl">Phone Number</FieldLabel>
                    <input type="tel" value={formData.phone} onChange={onPhoneChange} placeholder="(555) 555-5555" autoComplete="tel" className={inputClasses} data-id="element-605" data-mp="2ajyrafqiznvc" />
                  </label>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4" data-id="element-606" data-mp="2u87s1e64x26g">
                  {tcpaLoading ? <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-607" data-mp="13xcy9xlyo0b9">
                      <span className="w-4 h-4 border-2 border-gray-200 border-t-[#e8594f] rounded-full animate-spin" aria-hidden="true" data-id="element-608" data-mp="zv89bc6gjruy" />
                      Loading consent terms…
                    </div> : tcpaError ? <p className="text-sm text-red-600" data-id="element-609" data-mp="trldv277p7fa">{tcpaError}</p> : <label className="flex items-start gap-3 cursor-pointer" data-id="element-610" data-mp="g0q2nj98qe8j">
                      {/* Jornaya's checkbox disclosure pattern — the id ties
                          the displayed text to the LeadiD token. */}
                      <input id="leadid_tcpa_disclosure" type="checkbox" checked={formData.consent} onChange={e => onFieldChange('consent', e.target.checked)} className="mt-0.5 w-4 h-4 flex-shrink-0 rounded border-gray-300 text-[#e8594f] focus:ring-[#e8594f]" data-id="element-611" data-mp="65545v3drw3j" />
                      {tcpaDisclaimerHtml ? <span className="text-xs text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{
                  __html: tcpaDisclaimerHtml
                }} data-id="element-612" data-mp="1wdx2ud7joen5" /> : <span className="text-xs text-gray-600 leading-relaxed" data-id="element-613" data-mp="34rxtfvl83znh">
                          {FALLBACK_CONSENT} I also agree to the{' '}
                          <a href="/privacy-policy" className="underline hover:text-gray-900" data-id="element-614" data-mp="2v133oh8rqhxa">
                            Privacy Policy
                          </a>{' '}
                          and{' '}
                          <a href="/terms" className="underline hover:text-gray-900" data-id="element-615" data-mp="3nnnrj7q1cbw3">
                            Terms of Use
                          </a>
                          .
                        </span>}
                    </label>}
                </div>
              </div>}

            {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3" data-id="element-616" data-mp="22c5myik7gt6n">
                <p className="text-sm text-red-600" data-id="element-617" data-mp="1uruzsd3attw6">{error}</p>
              </div>}

            <div className={`mt-6 ${canGoBack ? 'flex gap-3' : ''}`} data-id="element-618" data-mp="1pt1wteln1la9">
              {canGoBack && <button type="button" onClick={onBack} className="px-6 py-3.5 rounded-xl border border-gray-200 bg-white font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300" data-id="element-619" data-mp="24q10wsha6vzz">
                  ← Back
                </button>}
              <button type="button" onClick={onContinue} disabled={addressChecking} className="flex-1 w-full px-6 py-3.5 rounded-xl bg-[#e8594f] font-bold text-white transition-colors hover:bg-[#d64a41] disabled:bg-[#e8594f]/60 disabled:cursor-wait focus:outline-none focus:ring-2 focus:ring-[#e8594f]/40" data-id="element-620" data-mp="170fvieryp2ia">
                {addressChecking ? 'Checking your address…' : isFinalStep ? 'See My Matches →' : 'Continue →'}
              </button>
            </div>

            {isFinalStep && <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-500" data-id="element-621" data-mp="1ms8fe8uzvuyo">
                <LockIcon className="w-3 h-3" aria-hidden="true" data-id="element-622" data-mp="1rrutekindf8j" />
                Your information is secure and confidential
              </p>}
          </div>
        </div>
      </main>

      <footer className="py-8 px-4" data-id="element-623" data-mp="z25s0620zd6e">
        <p className="text-center font-mono text-xs text-gray-500" data-id="element-624" data-mp="z37fxb453u5j">
          findonlinedegrees.org &nbsp;·&nbsp;
          <a href="/privacy-policy" className="underline hover:text-gray-700" data-id="element-625" data-mp="1443jfnkkxeaf">
            Privacy Policy
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/terms" className="underline hover:text-gray-700" data-id="element-626" data-mp="2fhyvuok3n8xy">
            Terms of Use
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/cookie-policy" className="underline hover:text-gray-700" data-id="element-627" data-mp="2kefmpqioo1eq">
            Cookie Policy
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/ca-notice" className="underline hover:text-gray-700" data-id="element-628" data-mp="3takztssp7bg">
            Do Not Sell My Info
          </a>
        </p>
        <p className="mt-3 text-center text-xs text-gray-500 max-w-2xl mx-auto" data-id="element-629" data-mp="3v76t8v726tu0">
          This page uses cookies and pixels from third parties, including Meta,
          for advertising and measurement. See our{' '}
          <a href="/cookie-policy" className="underline hover:text-gray-700" data-id="element-630" data-mp="3m94pi7vxxn31">
            Cookie Policy
          </a>{' '}
          for opt-out options.
        </p>
      </footer>
    </div>;
}